import React, { FC, Fragment } from 'react';
import { Box, ButtonAppearance, Spinner, Text, ToggleSwitch } from '@bechtel/gds';
import { User } from '@bechtel/types';
import { useName, useDate } from '@bechtel/client-common';
import { FormikProps } from 'formik';
import { AvatarRegularStyled } from '../styled';
import UserDetailItem from './item';
import { FooterButtonStyled, SepartorStyled, UserDetailFooterStyled } from './styled';
import { FormikState } from '~/user-admin/users/form-state';
import { useNavigate } from 'react-router-dom';
import { t, Trans } from '@lingui/macro';

interface UserInfoDetailProps {
  userDetail?: User | null;
  mutationLoading: boolean;
  formikProps: FormikProps<FormikState>;
  isProjectUser?: boolean;
  isNewUser?: boolean;
  isEditUser?: boolean;
}

export const UserInfoDetail: FC<UserInfoDetailProps> = ({
  userDetail,
  mutationLoading,
  isProjectUser,
  formikProps,
  isNewUser,
  isEditUser = false,
}) => {
  const navigate = useNavigate();
  const { formatISODate } = useDate();
  const { getFullName } = useName();
  const { setFieldValue, values, dirty, isValid } = formikProps;

  const imageUrl = userDetail?.crewMember?.imageUrl;
  const userFulllName = userDetail ? getFullName(userDetail.firstName, userDetail.lastName) : t`User Name`;

  return (
    <>
      {isProjectUser !== false ? (
        <>
          <Box data-testid="user-detail-header" pl={{ sm: '600' }}>
            {userDetail && userDetail.id.length > 0 && (
              <Box mr={{ sm: '500' }}>
                <AvatarRegularStyled
                  hasImageUrl={imageUrl ? true : false}
                  style={{ width: '56px', height: '56px' }}
                  src={imageUrl ? imageUrl : undefined}
                />
              </Box>
            )}
            <Box
              flexDirection={{ sm: 'column' }}
              flexGrow={{ sm: '1' }}
              style={{ pointerEvents: userDetail ? 'auto' : 'none' }}
            >
              <Box mb={{ sm: '550' }} style={{ whiteSpace: 'nowrap' }}>
                <Text family="graphik" size="500" color="grey.1000" weight="600">
                  {userFulllName}
                </Text>
              </Box>
              <Box>
                <Box mr={{ sm: '450' }}>
                  <ToggleSwitch
                    disabled={isNewUser}
                    isToggled={values.isActive}
                    onToggle={() => {
                      setFieldValue('isActive', !values.isActive);
                    }}
                  />
                </Box>
                <Text family="graphik" size="300" color="grey.1000">
                  {values.isActive ? t`Active` : t`Inactive`}
                  {userDetail && userDetail.updatedAt ? ` (Updated ${formatISODate(userDetail.updatedAt)})` : ''}
                </Text>
              </Box>
            </Box>
          </Box>
          {/* 42px is not availble in gds. (24px marginRight + 18px for scroll in UserDetalItem) */}
          <SepartorStyled ml={{ sm: '600' }} style={{ marginRight: '42px' }} mt={{ sm: '600' }} pt={{ sm: '300' }} />
          <UserDetailItem
            user={userDetail}
            formikProps={formikProps}
            isProjectUser={isProjectUser}
            isEditUser={isEditUser}
          />
        </>
      ) : (
        <UserDetailItem user={null} formikProps={formikProps} isProjectUser={isProjectUser} />
      )}

      <UserDetailFooterStyled>
        <Box>
          {(!userDetail || (userDetail && userDetail.id.length == 0)) && (
            <Box mr={{ sm: '400' }}>
              <FooterButtonStyled
                onClick={() => {
                  if (dirty) formikProps.handleReset();
                  else navigate(`user-admin`);
                }}
                disabled={mutationLoading}
                appearance={ButtonAppearance.SECONDARY}
              >
                {dirty ? t`Discard Changes` : t`Cancel`}
              </FooterButtonStyled>
            </Box>
          )}
          {userDetail && userDetail.id.length > 0 && (
            <Box mr={{ sm: '400' }}>
              <FooterButtonStyled
                onClick={() => {
                  formikProps.handleReset();
                }}
                disabled={mutationLoading || !dirty}
                appearance={ButtonAppearance.SECONDARY}
              >
                <Trans>Discard Changes</Trans>
              </FooterButtonStyled>
            </Box>
          )}
          <FooterButtonStyled
            data-testid="user-detail-save"
            onClick={() => {
              formikProps.handleSubmit();
            }}
            disabled={mutationLoading || !dirty || !isValid}
            appearance={ButtonAppearance.SECONDARY}
          >
            {mutationLoading && <Spinner />}
            {userDetail && userDetail.id.length > 0 && !mutationLoading && t`Save Changes`}
            {userDetail && userDetail.id.length == 0 && !mutationLoading && t`Activate user`}
            {!userDetail && !mutationLoading && t`Activate user`}
          </FooterButtonStyled>
        </Box>
      </UserDetailFooterStyled>
    </>
  );
};
